import React, { useState } from 'react'
import * as Sentry from '@sentry/react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { CheckIcon } from '@heroicons/react/24/solid'
import { pick } from 'lodash-es'

import { Card, CardContent } from 'shadcdn/ui/card'
import { Form } from 'shadcdn/ui/form'
import { ArrayTextAreaFormField, TextInputFormField, SelectFormField } from 'shadcdn/custom/Form'
import { VERSION_TYPE_SELECT_COLORMAP } from 'features/versions/constants'
import { useToast } from 'shadcdn/ui/use-toast'
import { Button } from 'ui/Button/Button'
import { SoftwareAdminApi, type VersionSchema } from 'openapi'
import { CONFIG } from 'config'
import { PromiseButton } from 'ui/Button/PromiseButton'

import { WarningBgIfEmpty } from './WarningBgIfEmpty'
import { AdminVersionFileUploadButton } from './AdminVersionFileUploadButton'

type Props = {
  version: VersionSchema
  onPostSave: () => void
}

const versionFormSchema = z.object({
  id: z.number().optional().nullable(),
  version: z.string(),
  type: z.string(),
  url: z.string(),
  components: z.array(z.string()),
  macInstallOrder: z.array(z.string()),
})
type AdminFormSchema = z.infer<typeof versionFormSchema>

export const AdminSoftwareVersionForm = React.memo((props: Props) => {
  const { version, onPostSave } = props

  const form = useForm<AdminFormSchema>({
    resolver: zodResolver(versionFormSchema),
    defaultValues: {
      ...pick(version, ['version', 'type', 'url', 'components', 'macInstallOrder']),
    },
  })

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const { toast } = useToast()

  async function onSubmit(values: AdminFormSchema) {
    setIsSubmitting(true)

    const updatedVersion = await SoftwareAdminApi.updateVersion({ id: version.id, requestBody: values })
    onPostSave()
    toast({
      // prettier-ignore
      title: (
        <div>
          <CheckIcon className="w-4 h-4 -t-1 inline" /> Saved
        </div>
      ) as any, // shitty cast
      className: 'bg-white !l-0 !t-0',
    })

    form.reset(updatedVersion)
    setIsSubmitting(false)
  }

  const handleParseVersionInfoClick: () => Promise<void> = async () => {
    if (!window.api) return
    const url: string = form.getValues()['url']

    Sentry.addBreadcrumb({ category: 'admin', message: 'ExtractVersionInfo', data: { url }, level: 'info' })

    // biome-ignore lint/suspicious/noImplicitAnyLet: fix later
    let versionInfo
    try {
      versionInfo = await window.api.extractVersionInfo(url)
    } catch (e: any) {
      if (e.message.includes('404')) {
        const description = (
          <>
            <div className="p-2 bg-gray-300 bg-opacity-50">{url}</div>Please recheck the url
          </>
        )
        toast({ title: 'Error while downloading file', description: description, className: 'bg-red-50' })
        return
      }
      toast({ title: 'Unknown error', description: 'Please contact developer', className: 'bg-red-50' })
      Sentry.captureException(e)
      return
    }
    form.setValue('macInstallOrder', versionInfo.installOrder)
    form.setValue('components', versionInfo.components)
    form.setValue('version', versionInfo.version)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8 flex-1 mt-4">
        <Card className="mx-auto relative">
          <CardContent className="flex flex-col gap-y-4 ">
            <a
              className="link absolute top-1 right-2 text-[10px] text-gray-500"
              href={`${CONFIG.API_BASE_URL}/admin/versions/version/${version.id}/change/`}
              target="_blank"
              rel="noreferrer"
            >
              Admin
            </a>
            <div className="flex flex-row gap-4 w-full">
              <div className="flex-grow flex flex-col gap-4">
                <div className="flex flex-row gap-x-4 pt-6 items-start">
                  <WarningBgIfEmpty checkValue={form.watch('url')} className="flex-grow">
                    <TextInputFormField
                      form={form}
                      name="url"
                      label={
                        <div className="flex">
                          <div className="flex-grow">Package URL</div>
                          <AdminVersionFileUploadButton
                            onUploadComplete={(url: string) => form.setValue('url', url)}
                            folder={`versions/${version.id}`}
                          />
                        </div>
                      }
                      className="text-xs"
                      disabled={isSubmitting}
                    />
                  </WarningBgIfEmpty>
                  <WarningBgIfEmpty checkValue={form.watch('version')}>
                    <TextInputFormField
                      form={form}
                      name="version"
                      label="Version"
                      disabled={isSubmitting}
                      className="w-32 text-xs"
                      growWrapClassName="text-xs font-mono"
                    />
                  </WarningBgIfEmpty>
                  <SelectFormField
                    form={form}
                    name="type"
                    label="Type"
                    placeholder="Select type"
                    className="w-32 text-xs"
                    disabled={isSubmitting}
                    colorMap={VERSION_TYPE_SELECT_COLORMAP}
                    options={[
                      { value: 'free', label: 'Free' },
                      { value: 'trial', label: 'Trial' },
                      { value: 'paid', label: 'Paid' },
                      { value: 'demo', label: 'Demo' },
                    ]}
                  />
                </div>

                <div className="flex gap-x-4">
                  <div className="flex-1">
                    <ArrayTextAreaFormField
                      form={form}
                      name="components"
                      label="Components"
                      className="[&_textarea]:text-[0.7rem] [&_textarea]:font-mono"
                      growWrapClassName="text-xs font-mono"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="flex-1">
                    <ArrayTextAreaFormField
                      form={form}
                      name="macInstallOrder"
                      label="Install Order"
                      className="[&_textarea]:text-[0.7rem] [&_textarea]:font-mono"
                      growWrapClassName="text-xs font-mono"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-4">
                <div className="flex-grow" />
                <div className="">
                  <PromiseButton
                    type="button"
                    size="sm"
                    color="light"
                    onClick={handleParseVersionInfoClick}
                    className="w-32"
                  >
                    Extract Data
                  </PromiseButton>
                </div>

                <div>
                  <Button type="submit" disabled={isSubmitting} spinner={isSubmitting} className="w-full">
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </form>
    </Form>
  )
})
